// packages/web/src/components/ChatWidget.tsx
// ═══════════════════════════════════════════════════════════════════
// Global "Chat with Us" floating widget — WebSocket-based
// Tier 0: Automated Bot → Tier 1: Org HR Admin → Tier 2: Platform Support
// ═══════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../context/AuthContext';

// ── Types ─────────────────────────────────────────────────────────
interface ChatMessage {
  type: 'message' | 'bot' | 'system' | 'action_chips' | 'typing' | 'status';
  from: string;
  fromName?: string;
  content: string;
  timestamp: string;
  chips?: string[];
  chatId?: string;
}

// ── WebSocket URL builder ─────────────────────────────────────────
function getWsUrl(token: string): string {
  const apiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';
  const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
  const host = apiUrl.replace(/^https?:\/\//, '');
  return `${wsProtocol}://${host}/ws/chat?token=${encodeURIComponent(token)}`;
}

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const location = useLocation();

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Connect to WebSocket
  const connect = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const ws = new WebSocket(getWsUrl(session.access_token));

      ws.onopen = () => {
        setIsConnected(true);
        // Send init with current path
        ws.send(JSON.stringify({
          type: 'init',
          currentPath: location.pathname,
        }));
      };

      ws.onmessage = (event) => {
        try {
          const msg: ChatMessage = JSON.parse(event.data);

          if (msg.type === 'typing') {
            setIsTyping(true);
            setTimeout(() => setIsTyping(false), 2000);
            return;
          }

          setMessages(prev => [...prev, msg]);

          // Increment unread if chat is closed
          if (!isOpen) {
            setUnreadCount(prev => prev + 1);
          }
        } catch {
          // ignore malformed messages
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        // Auto-reconnect after 3s
        reconnectTimeoutRef.current = window.setTimeout(() => {
          if (isOpen) connect();
        }, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };

      wsRef.current = ws;
    } catch (err) {
      console.warn('[ChatWidget] Connection error:', err);
    }
  }, [location.pathname, isOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      wsRef.current?.close();
    };
  }, []);

  // Connect when opening chat
  useEffect(() => {
    if (isOpen && !wsRef.current) {
      connect();
    }
  }, [isOpen, connect]);

  // Reset unread when opening
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  // ── Send message ────────────────────────────────────────────────
  const sendMessage = () => {
    if (!inputText.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    const msg: ChatMessage = {
      type: 'message',
      from: 'user',
      fromName: 'You',
      content: inputText.trim(),
      timestamp: new Date().toISOString(),
    };

    wsRef.current.send(JSON.stringify({
      type: 'message',
      content: inputText.trim(),
    }));

    setMessages(prev => [...prev, msg]);
    setInputText('');
  };

  // ── Handle chip click ──────────────────────────────────────────
  const handleChipClick = (chip: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    // Show user's choice
    setMessages(prev => [...prev, {
      type: 'message',
      from: 'user',
      fromName: 'You',
      content: chip,
      timestamp: new Date().toISOString(),
    }]);

    wsRef.current.send(JSON.stringify({
      type: 'chip_click',
      chip,
    }));
  };

  // ── Send typing indicator ──────────────────────────────────────
  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing' }));
    }
  };

  // ── Render message bubble ──────────────────────────────────────
  const renderMessage = (msg: ChatMessage, idx: number) => {
    const isUser = msg.from === 'user';
    const isSystem = msg.type === 'system';
    const isBot = msg.from === 'bot';
    const time = new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    if (isSystem) {
      return (
        <div key={idx} style={{
          textAlign: 'center', padding: '0.5rem', margin: '0.25rem 0',
          fontSize: '0.7rem', color: '#6b7280', fontStyle: 'italic',
        }}>
          {msg.content}
        </div>
      );
    }

    return (
      <div key={idx} style={{
        display: 'flex', flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        marginBottom: '0.5rem',
      }}>
        {!isUser && (
          <span style={{ fontSize: '0.65rem', color: '#9ca3af', marginBottom: '0.15rem', marginLeft: '0.25rem' }}>
            {isBot ? '🤖 ' : '👤 '}{msg.fromName || 'Support'}
          </span>
        )}
        <div style={{
          maxWidth: '80%', padding: '0.6rem 0.85rem', borderRadius: 12,
          background: isUser ? '#2563eb' : isBot ? '#f3f4f6' : '#ecfdf5',
          color: isUser ? '#fff' : '#1f2937',
          fontSize: '0.82rem', lineHeight: 1.45,
          borderBottomRightRadius: isUser ? 2 : 12,
          borderBottomLeftRadius: isUser ? 12 : 2,
          wordBreak: 'break-word',
        }}>
          {msg.content}
        </div>

        {/* Action Chips */}
        {msg.type === 'action_chips' && msg.chips && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.4rem' }}>
            {msg.chips.map((chip, ci) => (
              <button
                key={ci}
                onClick={() => handleChipClick(chip)}
                style={{
                  padding: '0.3rem 0.65rem', borderRadius: 16,
                  border: '1px solid #d1d5db', background: '#fff',
                  fontSize: '0.72rem', color: '#2563eb', cursor: 'pointer',
                  fontWeight: 500, transition: 'all 0.15s',
                }}
                onMouseOver={(e) => {
                  (e.target as HTMLButtonElement).style.background = '#eff6ff';
                  (e.target as HTMLButtonElement).style.borderColor = '#93c5fd';
                }}
                onMouseOut={(e) => {
                  (e.target as HTMLButtonElement).style.background = '#fff';
                  (e.target as HTMLButtonElement).style.borderColor = '#d1d5db';
                }}
              >
                {chip}
              </button>
            ))}
          </div>
        )}

        <span style={{ fontSize: '0.6rem', color: '#9ca3af', marginTop: '0.15rem', marginLeft: '0.25rem', marginRight: '0.25rem' }}>
          {time}
        </span>
      </div>
    );
  };

  // ── Floating Toggle Button ─────────────────────────────────────
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        aria-label="Chat with us"
        style={{
          position: 'fixed', bottom: 80, right: 24, zIndex: 9999,
          width: 56, height: 56, borderRadius: '50%',
          background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
          border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(37, 99, 235, 0.4)',
          transition: 'transform 0.2s, box-shadow 0.2s',
        }}
        onMouseOver={(e) => {
          (e.currentTarget).style.transform = 'scale(1.1)';
          (e.currentTarget).style.boxShadow = '0 6px 24px rgba(37, 99, 235, 0.5)';
        }}
        onMouseOut={(e) => {
          (e.currentTarget).style.transform = 'scale(1)';
          (e.currentTarget).style.boxShadow = '0 4px 20px rgba(37, 99, 235, 0.4)';
        }}
      >
        <span style={{ fontSize: 26, color: '#fff' }}>💬</span>
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            background: '#ef4444', color: '#fff', borderRadius: '50%',
            minWidth: 20, height: 20, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            fontSize: '0.65rem', fontWeight: 700,
            border: '2px solid #fff',
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
    );
  }

  // ── Chat Window ────────────────────────────────────────────────
  return (
    <div style={{
      position: 'fixed', bottom: 80, right: 24, zIndex: 9999,
      width: 380, height: 520,
      background: '#fff', borderRadius: 16,
      boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden', border: '1px solid #e5e7eb',
    }}>
      {/* Header */}
      <div style={{
        padding: '0.85rem 1rem',
        background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
        color: '#fff', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: 22 }}>💬</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Chat with Us</div>
            <div style={{ fontSize: '0.65rem', opacity: 0.85 }}>
              {isConnected ? '🟢 Online' : '🔴 Connecting...'}
            </div>
          </div>
        </div>
        <button
          onClick={() => {
            setIsOpen(false);
            wsRef.current?.close();
            wsRef.current = null;
            setMessages([]);
          }}
          style={{
            background: 'rgba(255,255,255,0.15)', border: 'none',
            color: '#fff', width: 32, height: 32, borderRadius: '50%',
            cursor: 'pointer', fontSize: 18, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}
          aria-label="Close chat"
        >
          ✕
        </button>
      </div>

      {/* Messages */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '0.75rem',
        background: '#fafbfc',
      }}>
        {messages.length === 0 && (
          <div style={{
            textAlign: 'center', padding: '2rem 1rem',
            color: '#9ca3af', fontSize: '0.8rem',
          }}>
            Connecting to Fincella Assistant...
          </div>
        )}

        {messages.map((msg, idx) => renderMessage(msg, idx))}

        {isTyping && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.25rem',
            padding: '0.4rem 0.6rem', fontSize: '0.7rem', color: '#9ca3af',
          }}>
            <span className="typing-dot">●</span>
            <span className="typing-dot" style={{ animationDelay: '0.2s' }}>●</span>
            <span className="typing-dot" style={{ animationDelay: '0.4s' }}>●</span>
            <style>{`
              @keyframes typingBounce { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
              .typing-dot { animation: typingBounce 1s ease-in-out infinite; }
            `}</style>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{
        padding: '0.6rem', borderTop: '1px solid #e5e7eb',
        display: 'flex', gap: '0.5rem', background: '#fff',
      }}>
        <input
          type="text"
          value={inputText}
          onChange={(e) => {
            setInputText(e.target.value);
            handleTyping();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
          placeholder="Type a message..."
          style={{
            flex: 1, padding: '0.55rem 0.75rem', borderRadius: 20,
            border: '1px solid #d1d5db', fontSize: '0.82rem',
            outline: 'none', transition: 'border-color 0.15s',
          }}
          onFocus={(e) => { e.target.style.borderColor = '#2563eb'; }}
          onBlur={(e) => { e.target.style.borderColor = '#d1d5db'; }}
        />
        <button
          onClick={sendMessage}
          disabled={!inputText.trim() || !isConnected}
          style={{
            width: 36, height: 36, borderRadius: '50%',
            background: inputText.trim() && isConnected ? '#2563eb' : '#d1d5db',
            border: 'none', color: '#fff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, transition: 'background 0.15s',
          }}
          aria-label="Send message"
        >
          ➤
        </button>
      </div>
    </div>
  );
}
